// Copyright (c) Docugami, Inc. All rights reserved.

import { TokenIdToBox } from "store/shared_types";
import { Page } from "./Page";
import { Point } from "sdv/Types";
import { EntityId } from "sdv/engine/Entity";
import { Mode } from "./Mode";
import { Event } from "utils/Event";
import { CanvasSelectionMode } from "store/documents/types";
import { IDocumentDgml } from "hooks/documentDgml";
import { SelectionFilters } from "./SelectionFilters";
import { SearchState } from "./SearchState";
import { PageInfo, TokenRange } from "networking/api";

/**
 * An individual resource is represented by its value and whether it has changed this frame
 */
export interface Resource<T> {
    value: T;
    updated: boolean;
}

export enum PageData {
    Image = 1 << 0,
    TokenLocations = 1 << 1,
    All = Image | TokenLocations
}

export interface ResourceTypes {
    rawPages: PageInfo[];
    pages: Page[];
    pageLoadState: Record<string, number>
    visiblePages: Page[];
    pageImages: {[id: string]: HTMLImageElement};
    pageTokens: TokenIdToBox;
    pagesLoaded: boolean;
    worldSpace: { width: number, height: number };
    viewport: { width: number, height: number };
    viewWidth: number;
    viewHeight: number;
    viewPosition: Point;
    rootControl: EntityId | null;
    mode: Mode;
    documentId: string;
    selectedEntityName: string;
    tool: CanvasSelectionMode;
    dgml: IDocumentDgml | null;
    focusedPage: Page | null;
    selectionFilters: SelectionFilters | null;
    searchState: SearchState | null;
    featureFlags: { [id: string]: boolean };
    chunkToolbarHoverSpanId: string | undefined;
    resizeDisabled: boolean;
    controlRenderDisabled: boolean;
    dpr: number;
    textVersion: string | undefined;
    previewChunks: ChunkToColorMap;
    multiSelect: boolean;
    externalSelections: MultiSelectChunk[];
}

export type MultiSelectChunk = {
    spanId: string;
    tokenRange: TokenRange;
}

type ChunkToColorMap = { [spanId: string]: string };

export type ResourceDependencies = (keyof ResourceTypes)[];

export interface IResources {
    get<Key extends keyof(ResourceTypes)>(key: Key): ResourceMap[Key];
    values<Key extends keyof(ResourceTypes)>(keys: Key[]): Partial<ResourceTypes>;
    set<Key extends keyof(ResourceTypes)>(key: Key, value: ResourceTypes[Key]): boolean;
    setFn<Key extends keyof(ResourceTypes)>(key: Key, fn: (oldValue: ResourceTypes[Key]) => ResourceTypes[Key]): boolean;
    check<Key extends keyof(ResourceTypes)>(keys: Key[] | Key): boolean;
}

export interface IResourcesView {
    getView<Key extends keyof(ResourceTypes)>(key: Key): ResourceMap[Key];
    addListener<Key extends keyof(ResourceTypes)>(key: Key, callback: (value: ResourceTypes[Key]) => void): () => void;
}

type ResourceMap = {
    [Key in keyof(ResourceTypes)] : Resource<ResourceTypes[Key]>
}

type ResourceListenerMap = {
    [Key in keyof(ResourceTypes)]? : Event<ResourceTypes[Key]>
}

type ResourceEqualityCheckMap = {
    [Key in keyof(ResourceTypes)]? : (oldValue: ResourceTypes[Key], newValue: ResourceTypes[Key]) => boolean
}

type ResourceSetterMap = {
    [Key in keyof(ResourceTypes)]? : (value: ResourceTypes[Key]) => ResourceTypes[Key]
}

export const defaultResourceTypes: ResourceTypes = {
    rawPages: [],
    pages: [],
    pageLoadState : {},
    visiblePages: [],
    pageImages: {},
    pageTokens: {},
    pagesLoaded: false,
    worldSpace: { width: 0, height: 0 },
    viewWidth: 0,
    viewHeight: 0,
    viewPosition: {x: 0, y: 0},
    viewport: {width: 0, height: 0},
    rootControl: null,
    mode: Mode.Selection,
    documentId: '',
    selectedEntityName: '',
    tool: CanvasSelectionMode.Select,
    dgml: null,
    focusedPage: null,
    selectionFilters: null,
    searchState: null,
    featureFlags: {},
    chunkToolbarHoverSpanId: undefined,
    resizeDisabled: false,
    controlRenderDisabled: false,
    dpr: 1,
    textVersion: undefined,
    previewChunks: {},
    multiSelect: false,
    externalSelections: []
}

const equalityChecks: ResourceEqualityCheckMap = {
    viewPosition: (a, b) => a.x === b.x && a.y === b.y,
    worldSpace: (a, b) => a.width === b.width && a.height === b.height
}

const resourceSetter: ResourceSetterMap = {
    viewPosition: a => ({...a}),
    worldSpace: a => ({...a}),
    viewport: a => ({...a}),
}

const constructResourceMap = (): ResourceMap => {
    return {
        rawPages: defaultResource("rawPages"),
        pages: defaultResource("pages"),
        pageLoadState: defaultResource("pageLoadState"),
        visiblePages: defaultResource("visiblePages"),
        pageImages: defaultResource("pageImages"),
        pageTokens: defaultResource("pageTokens"),
        pagesLoaded: defaultResource("pagesLoaded"),
        worldSpace: defaultResource("worldSpace"),
        viewWidth: defaultResource("viewWidth"),
        viewHeight: defaultResource("viewHeight"),
        viewPosition: defaultResource("viewPosition"),
        viewport: defaultResource("viewport"),
        rootControl: defaultResource("rootControl"),
        mode: defaultResource("mode"),
        documentId: defaultResource("documentId"),
        selectedEntityName: defaultResource("selectedEntityName"),
        tool: defaultResource("tool"),
        dgml: defaultResource("dgml"),
        focusedPage: defaultResource("focusedPage"),
        selectionFilters: defaultResource("selectionFilters"),
        searchState: defaultResource("searchState"),
        featureFlags: defaultResource("featureFlags"),
        chunkToolbarHoverSpanId: defaultResource("chunkToolbarHoverSpanId"),
        resizeDisabled: defaultResource("resizeDisabled"),
        controlRenderDisabled: defaultResource("controlRenderDisabled"),
        dpr: defaultResource("dpr"),
        textVersion: defaultResource("textVersion"),
        previewChunks: defaultResource("previewChunks"),
        multiSelect: defaultResource("multiSelect"),
        externalSelections: defaultResource("externalSelections")
    }
}

function defaultResource<Key extends keyof(ResourceTypes)>(key: Key): Resource<ResourceTypes[Key]> {
    return {
        value : defaultResourceTypes[key],
        updated: true
    }
}

/**
 * Resources represent data that is typically used across the React/Engine boundary.
 * They provider per frame update semantics as well as callbacks for external listeners.
 * Systems that perform expensive operations can also use resource changes to trigger
 * the expensive operations only when required.
 */
export class Resources implements IResources, IResourcesView {
    private readonly _resources: ResourceMap = constructResourceMap();
    private _updated: Set<keyof ResourceTypes> = new Set<keyof ResourceTypes>();
    private _listeners: ResourceListenerMap = {};

    getView<Key extends keyof(ResourceTypes)>(key: Key): ResourceMap[Key] {
        return { ...this._resources[key] };
    }

    addListener<Key extends keyof(ResourceTypes)>(key: Key, callback: (value: ResourceTypes[Key]) => void): () => void {
        let event = this._listeners[key];
        if (!event) {
            event = new Event<ResourceTypes[Key]>() as ResourceListenerMap[Key];
            this._listeners[key] = event;
        }

        return event!.addListener(callback);
    }

    get<Key extends keyof(ResourceTypes)>(key: Key): ResourceMap[Key] {
        return this._resources[key];
    }

    values<Key extends keyof(ResourceTypes)>(keys: Key[]): Partial<ResourceTypes> {
        return Object.fromEntries(keys.map(k => [k, this._resources[k].value]));
    }

    set<Key extends keyof(ResourceTypes)>(key: Key, value: ResourceTypes[Key]): boolean {
        const resource = this._resources[key];
        const defaultCheck = (a: ResourceTypes[Key], b: ResourceTypes[Key]) => a === b;
        const equalityCheck = equalityChecks[key] ?? defaultCheck;
        if (!equalityCheck(resource.value, value)) {
            this._updated.add(key);

            const defaultSetter = (a: ResourceTypes[Key]) => a;
            const setter = resourceSetter[key] ?? defaultSetter
            resource.value = setter(value);
            return true;
        }

        return false;
    }

    setFn<Key extends keyof(ResourceTypes)>(key: Key, fn: (oldValue: ResourceTypes[Key]) => ResourceTypes[Key]): boolean {
        const resource = this._resources[key];
        return this.set(key, fn(resource.value));
    }

    check<Key extends keyof(ResourceTypes)>(keys: Key[] | Key): boolean {
        if (keys instanceof Array) {
            for (const key of keys) {
                if (this._resources[key].updated) {
                    return true;
                }
            }
        }
        else {
            return !!this._resources[keys].updated;
        }

        return false;
    }

    preUpdate() {
        for (const [key, val] of Object.entries(this._resources)) {
            val.updated = this._updated.has(key as keyof ResourceTypes);
        }

        this._updated.clear();
    }

    postUpdate() {
        for (const [key, resource] of Object.entries(this._resources)) {
            if (resource.updated) {
                const event = this._listeners[key as keyof ResourceTypes] as Event<any> | undefined;
                event?.raise(resource.value);
            }

            resource.updated = false;
        }
    }
}
